import { useEffect } from 'react';
import type { SlideshowController } from './useSlideshowController';

/**
 * Every keystroke the presentation answers, in one place.
 *
 * Escape works in both surfaces: it ends the presentation outright, whether
 * that's from fullscreen or the panel (Sept 9 2026 client decision; Dock is
 * the only way from fullscreen to the panel now). Arrow keys and Space are
 * fullscreen only — the panel leaves the address search bar and the map
 * interactive underneath it, so a global arrow/space handler there would
 * eat keystrokes meant for a text field. Fullscreen is a real dialog that
 * takes focus on open, so it is safe there.
 *
 * Called once, by MapSlideshow, rather than by each surface: the surfaces
 * mount and unmount as the stage changes and neither should have to know
 * which keys the other one claims.
 */
export function useSlideshowKeys(controller: SlideshowController): void {
  const { stage, next, prev, close, exitFullscreen } = controller;

  useEffect(() => {
    if (stage === 'closed') return;
    const isFullscreen = stage === 'fullscreen';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) exitFullscreen();
        else close();
        return;
      }
      if (!isFullscreen) return;
      if (e.key === 'ArrowLeft') {
        prev();
        return;
      }
      if (e.key === 'ArrowRight') {
        next();
        return;
      }
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        next();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [stage, next, prev, close, exitFullscreen]);
}
